/**
 * useUser Composable
 * 
 * A composable that provides user-related functionality and state management.
 * It handles user data access, authentication state, and user initials generation.
 * 
 * Features:
 * - Current user data access
 * - Authentication state checking
 * - User initials generation from name
 * 
 * @returns {Object} An object containing user data, auth state, and user initials
 */

import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

export function useUser() {
  const authStore = useAuthStore()

  const userInitials = computed(() => {
    const user = authStore.currentUser
    if (!user?.user_details) return ''
    
    const { first_name, last_name } = user.user_details
    const initials = `${first_name?.[0] || ''}${last_name?.[0] || ''}`
    return initials.toUpperCase()
  })

  const userName = computed(() => {
    const user = authStore.currentUser
    let name = user?.user_details?.full_name || user?.email || ''

    if (user?.user_type_label === 'employee' && name) {
      name = name
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }

    return name
  })

  const userFirstName = computed(() => {
    return authStore.currentUser?.user_details?.first_name || ''
  })

  const userLastName = computed(() => {
    return authStore.currentUser?.user_details?.last_name || ''
  })

  const userMiddleName = computed(() => {
    return authStore.currentUser?.user_details?.middle_name || ''
  })

  const userCity = computed(() => {
    return authStore.currentUser?.user_details?.city || ''
  })

  const userState = computed(() => {
    return authStore.currentUser?.user_details?.state || ''
  })

  const userZipCode = computed(() => {
    return authStore.currentUser?.user_details?.zip_code || ''
  })

  const userPhoneNumber = computed(() => {
    return authStore.currentUser?.user_details?.phone_number || ''
  })

  const userStreetAddress = computed(() => {
    return authStore.currentUser?.user_details?.street_address || ''
  })

  const userPosition = computed(() => {
    return authStore.currentUser?.user_details?.position || ''
  })

  const userDepartment = computed(() => {
    return authStore.currentUser?.user_details?.department || ''
  })

  const userHireDate = computed(() => {
    return authStore.currentUser?.user_details?.hire_date || ''
  })

  const userDateOfBirth = computed(() => {
    return authStore.currentUser?.user_details?.date_of_birth || ''
  })

  const userGender = computed(() => {
    return authStore.currentUser?.user_details?.gender || ''
  })

  const userEmergencyContactName = computed(() => {
    return authStore.currentUser?.user_details?.emergency_contact_name || ''
  })

  const userEmergencyContactPhone = computed(() => {
    return authStore.currentUser?.user_details?.emergency_contact_phone || ''
  })

  const userEmergencyContactRelation = computed(() => {
    return authStore.currentUser?.user_details?.emergency_contact_relation || ''
  })

  const userTotalDocuments = computed(() => {
    return authStore.currentUser?.total_documents || 0
  })

  const userEmail = computed(() => {
    return authStore.currentUser?.email || ''
  })

  const userImage = computed(() => {
    return authStore.currentUser?.user_details?.user_image || ''
  })

  const userType = computed(() => {
    return authStore.currentUser?.user_type_label || ''
  })

  const isOwner = computed(() => {
    return authStore.isOwner
  })

  const isAdmin2 = computed(() => {
    return authStore.isAdmin2
  })

  const canManageBusinessDocuments = computed(() => {
    return authStore.canManageBusinessDocuments
  })

  const hasBusinessDocumentsAccess = computed(() => {
    return authStore.hasBusinessDocumentsAccess
  })

  const userRoleLabel = computed(() => {
    if (authStore.isAdmin) {
      if (authStore.isOwner) {
        return 'Owner Access'
      }
      const position = authStore.currentUser?.user_details?.position
      if (position) {
        return position
      }
      return 'Admin Access'
    }
    return 'Staff'
  })

  const isActive = computed(() => {
    return authStore.currentUser?.user_status || false
  })

  const passwordChangedTimeAgo = computed(() => {
    return authStore.currentUser?.password_changed_time_ago || 'Never changed'
  })

  const currentUser = computed(() => authStore.currentUser)
  const isAuthenticated = computed(() => authStore.isAuthenticated)

  return {
    userInitials,
    userName,
    userEmail,
    userImage,
    userType,
    isOwner,
    isAdmin2,
    canManageBusinessDocuments,
    hasBusinessDocumentsAccess,
    userRoleLabel,
    currentUser,
    isAuthenticated,
    isActive,
    userFirstName,
    userLastName,
    userMiddleName,
    userStreetAddress,
    userCity,
    userState,
    userZipCode,
    userPhoneNumber,
    userPosition,
    userDepartment,
    userHireDate,
    userDateOfBirth,
    userGender,
    userEmergencyContactName,
    userEmergencyContactPhone,
    userEmergencyContactRelation,
    userTotalDocuments,
    passwordChangedTimeAgo
  }
} 